import React from 'react';
import { Plus, Edit, Key, Trash2, MapPin, Shield } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';

export const UserManagementPanel = ({
  users = [],
  currentUserId,
  // Create user
  showCreateUserDialog,
  setShowCreateUserDialog,
  newUser,
  setNewUser,
  onCreateUser,
  // Edit user
  showEditUserDialog,
  setShowEditUserDialog,
  selectedUser,
  editUser,
  setEditUser,
  onEditUser,
  onOpenEditDialog,
  // Password
  showPasswordDialog,
  setShowPasswordDialog,
  newPassword,
  setNewPassword,
  onChangePassword,
  onOpenPasswordDialog,
  // Delete
  onDeleteUser,
  // Roles available
  availableRoles = ['operator', 'technician', 'd2d_agent'],
  // Groups (for Admin)
  availableGroups = [],
  showGroupSelection = false
}) => {
  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-700';
      case 'operator': return 'bg-blue-100 text-blue-700';
      case 'accountant': return 'bg-purple-100 text-purple-700';
      case 'd2d_agent': return 'bg-orange-100 text-orange-700';
      case 'technician': return 'bg-green-100 text-green-700';
      case 'lawyer': return 'bg-amber-100 text-amber-700';
      case 'network_admin': return 'bg-cyan-100 text-cyan-700';
      case 'system_admin': return 'bg-slate-100 text-slate-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin': return 'Admin';
      case 'operator': return 'Operator';
      case 'accountant': return 'Mühasib';
      case 'd2d_agent': return 'D2D Agent';
      case 'technician': return 'Texnik';
      case 'lawyer': return 'Hüquqşunas';
      case 'network_admin': return 'NetAdmin';
      case 'system_admin': return 'SysAdmin';
      default: return role;
    }
  };

  const getGroupNames = (userGroups) => {
    if (!userGroups || userGroups.length === 0) return '-';
    const visibleGroups = userGroups
      .map(gId => availableGroups.find(g => g.id === gId))
      .filter(g => g !== undefined);
    
    if (visibleGroups.length === 0) return '-';
    return visibleGroups.map(g => g.name).join(', ');
  };

  // Get user's roles (from roles array or single role)
  const getUserRoles = (user) => {
    if (user.roles && user.roles.length > 0) {
      return user.roles;
    }
    return user.role ? [user.role] : [];
  };

  // Exclusive roles (only one can be selected)
  const exclusiveRoles = ['admin', 'operator', 'accountant', 'lawyer', 'network_admin', 'system_admin'];
  // Combinable roles (can be selected together)
  const combinableRoles = ['technician', 'd2d_agent'];

  const handleRoleToggle = (role, isCreate = true) => {
    const currentRoles = isCreate ? (newUser.roles || []) : (editUser.roles || []);
    
    if (exclusiveRoles.includes(role)) {
      // Exclusive role selected - clear all and set only this one
      if (isCreate) {
        setNewUser({ ...newUser, roles: [role], role: role });
      } else {
        setEditUser({ ...editUser, roles: [role] });
      }
    } else {
      // Combinable role (technician/d2d_agent)
      // First, remove any exclusive roles
      let newRoles = currentRoles.filter(r => !exclusiveRoles.includes(r));
      
      if (newRoles.includes(role)) {
        // Remove this role
        newRoles = newRoles.filter(r => r !== role);
      } else {
        // Add this role
        newRoles = [...newRoles, role];
      }
      
      if (isCreate) {
        setNewUser({ ...newUser, roles: newRoles, role: newRoles[0] || 'technician' });
      } else {
        setEditUser({ ...editUser, roles: newRoles });
      }
    }
  };

  const isRoleSelected = (role, isCreate = true) => {
    const roles = isCreate ? (newUser.roles || []) : (editUser.roles || []);
    return roles.includes(role);
  };

  const canSubmitCreate = () => {
    const hasRole = (newUser.roles || []).length > 0;
    const hasGroup = !showGroupSelection || (newUser.groups || []).length > 0;
    return hasRole && hasGroup;
  };

  const canSubmitEdit = () => {
    const hasRole = (editUser.roles || []).length > 0;
    const hasGroup = !showGroupSelection || (editUser.groups || []).length > 0;
    return hasRole && hasGroup;
  };

  const handleGroupToggle = (groupId, isCreate = true) => {
    if (isCreate) {
      const currentGroups = newUser.groups || [];
      if (currentGroups.includes(groupId)) {
        setNewUser({ ...newUser, groups: currentGroups.filter(id => id !== groupId) });
      } else {
        setNewUser({ ...newUser, groups: [...currentGroups, groupId] });
      }
    } else {
      const currentGroups = editUser.groups || [];
      if (currentGroups.includes(groupId)) {
        setEditUser({ ...editUser, groups: currentGroups.filter(id => id !== groupId) });
      } else {
        setEditUser({ ...editUser, groups: [...currentGroups, groupId] });
      }
    }
  };

  const isGroupSelected = (groupId, isCreate = true) => {
    if (isCreate) {
      return (newUser.groups || []).includes(groupId);
    }
    return (editUser.groups || []).includes(groupId);
  };

  // Filter available roles for display
  const singleSelectRoles = availableRoles.filter(r => exclusiveRoles.includes(r));
  const multiSelectRoles = availableRoles.filter(r => combinableRoles.includes(r));

  // Available modules
  const availableModules = [
    { id: 'services', label: 'Servislər' },
    { id: 'connections', label: 'Qoşulmalar' },
    { id: 'd2d', label: 'D2D Qeydləri' },
    { id: 'performance', label: 'Texnik Performans' },
    { id: 'reports', label: 'Hesabatlar' },
    { id: 'map', label: 'Xəritə' },
    { id: 'hr', label: 'HR / Davamiyyət' },
    { id: 'accounting', label: 'Mühasibatlıq' },
    { id: 'user_management', label: 'İstifadəçi İdarəetmə' },
    { id: 'group_management', label: 'Qrup İdarəetmə' },
  ];

  const handleModuleToggle = (moduleId, isCreate = true) => {
    if (isCreate) {
      const current = newUser.modules || [];
      if (current.includes(moduleId)) {
        setNewUser({ ...newUser, modules: current.filter(m => m !== moduleId) });
      } else {
        setNewUser({ ...newUser, modules: [...current, moduleId] });
      }
    } else {
      const current = editUser.modules || [];
      if (current.includes(moduleId)) {
        setEditUser({ ...editUser, modules: current.filter(m => m !== moduleId) });
      } else {
        setEditUser({ ...editUser, modules: [...current, moduleId] });
      }
    }
  };

  const toggleAllModules = (isCreate = true) => {
    const allIds = availableModules.map(m => m.id);
    if (isCreate) {
      const current = newUser.modules || [];
      const allSelected = allIds.every(id => current.includes(id));
      setNewUser({ ...newUser, modules: allSelected ? [] : allIds });
    } else {
      const current = editUser.modules || [];
      const allSelected = allIds.every(id => current.includes(id));
      setEditUser({ ...editUser, modules: allSelected ? [] : allIds });
    }
  };

  const getModuleNames = (userModules) => {
    if (!userModules || userModules.length === 0) return 'Hamısı';
    return userModules.map(m => availableModules.find(am => am.id === m)?.label || m).join(', ');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-zinc-900">İstifadəçi İdarəetməsi</h2>
        <Dialog open={showCreateUserDialog} onOpenChange={setShowCreateUserDialog}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90 text-white" data-testid="create-user-button">
              <Plus className="h-4 w-4 mr-2" />
              Yeni İstifadəçi
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="create-user-dialog" className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">Yeni İstifadəçi Yarat</DialogTitle>
            </DialogHeader>
            <form onSubmit={onCreateUser} className="space-y-4">
              <div>
                <Label htmlFor="username">İstifadəçi adı (Login)</Label>
                <Input
                  id="username"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  required
                  className="mt-1.5"
                  data-testid="username-input"
                  placeholder="operator1"
                />
              </div>
              <div>
                <Label htmlFor="full_name">Ad Soyad</Label>
                <Input
                  id="full_name"
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                  className="mt-1.5"
                  placeholder="Əli Məmmədov"
                />
              </div>
              <div>
                <Label htmlFor="phone">Telefon</Label>
                <Input
                  id="phone"
                  value={newUser.phone}
                  onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                  className="mt-1.5"
                  placeholder="+994501234567"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="mt-1.5"
                  placeholder="ali@example.com"
                />
              </div>
              <div>
                <Label htmlFor="password">Şifrə</Label>
                <Input
                  id="password"
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  required
                  className="mt-1.5"
                  data-testid="password-input"
                />
              </div>
              
              {/* Role Selection */}
              <div>
                <Label>Rol Seçin *</Label>
                <p className="text-xs text-zinc-500 mt-1 mb-2">Ən az bir rol seçilməlidir</p>
                <div className="mt-1.5 p-3 bg-zinc-50 rounded-lg border space-y-3">
                  {/* Exclusive roles (single select behavior) */}
                  {singleSelectRoles.length > 0 && (
                    <div className="space-y-2">
                      {singleSelectRoles.map(role => (
                        <div key={role} className="flex items-center space-x-2">
                          <Checkbox
                            id={`create-role-${role}`}
                            checked={isRoleSelected(role, true)}
                            onCheckedChange={() => handleRoleToggle(role, true)}
                            data-testid={`create-role-checkbox-${role}`}
                          />
                          <label
                            htmlFor={`create-role-${role}`}
                            className="text-sm font-medium leading-none cursor-pointer"
                          >
                            {getRoleLabel(role)}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Separator if both types exist */}
                  {singleSelectRoles.length > 0 && multiSelectRoles.length > 0 && (
                    <div className="border-t border-zinc-200 pt-2">
                      <p className="text-xs text-zinc-500 mb-2">Aşağıdakılar birlikdə seçilə bilər:</p>
                    </div>
                  )}
                  
                  {/* Combinable roles (multi select) */}
                  {multiSelectRoles.length > 0 && (
                    <div className="space-y-2">
                      {multiSelectRoles.map(role => (
                        <div key={role} className="flex items-center space-x-2">
                          <Checkbox
                            id={`create-role-${role}`}
                            checked={isRoleSelected(role, true)}
                            onCheckedChange={() => handleRoleToggle(role, true)}
                            data-testid={`create-role-checkbox-${role}`}
                          />
                          <label
                            htmlFor={`create-role-${role}`}
                            className="text-sm font-medium leading-none cursor-pointer"
                          >
                            {getRoleLabel(role)}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {(newUser.roles || []).length === 0 && (
                  <p className="text-xs text-red-500 mt-1">Ən az bir rol seçin</p>
                )}
              </div>
              
              {/* Group Selection for Admin */}
              {showGroupSelection && availableGroups.length > 0 && (
                <div>
                  <Label className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Qrup Seçin *
                  </Label>
                  <p className="text-xs text-zinc-500 mt-1 mb-2">Ən az bir qrup seçilməlidir</p>
                  <div className="mt-1.5 space-y-2 p-3 bg-zinc-50 rounded-lg border max-h-40 overflow-y-auto">
                    {availableGroups.map(group => (
                      <div key={group.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`create-group-${group.id}`}
                          checked={isGroupSelected(group.id, true)}
                          onCheckedChange={() => handleGroupToggle(group.id, true)}
                          data-testid={`create-group-checkbox-${group.id}`}
                        />
                        <label
                          htmlFor={`create-group-${group.id}`}
                          className="text-sm font-medium leading-none cursor-pointer"
                        >
                          {group.name}
                        </label>
                      </div>
                    ))}
                  </div>
                  {(newUser.groups || []).length === 0 && (
                    <p className="text-xs text-red-500 mt-1">Ən az bir qrup seçin</p>
                  )}
                </div>
              )}

              {/* Module Permissions */}
              <div>
                <Label className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Modul İcazələri
                </Label>
                <p className="text-xs text-zinc-500 mt-1 mb-2">Boş buraxılsa bütün modullara giriş olacaq</p>
                <div className="mt-1.5 p-3 bg-zinc-50 rounded-lg border space-y-2">
                  <div className="flex items-center space-x-2 pb-2 border-b border-zinc-200">
                    <Checkbox
                      id="create-module-all"
                      checked={availableModules.every(m => (newUser.modules || []).includes(m.id))}
                      onCheckedChange={() => toggleAllModules(true)}
                      data-testid="create-module-all"
                    />
                    <label htmlFor="create-module-all" className="text-sm font-semibold leading-none cursor-pointer">
                      Hamısını seç
                    </label>
                  </div>
                  {availableModules.map(mod => (
                    <div key={mod.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`create-module-${mod.id}`}
                        checked={(newUser.modules || []).includes(mod.id)}
                        onCheckedChange={() => handleModuleToggle(mod.id, true)}
                        data-testid={`create-module-${mod.id}`}
                      />
                      <label htmlFor={`create-module-${mod.id}`} className="text-sm font-medium leading-none cursor-pointer">
                        {mod.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-primary hover:bg-primary/90" 
                data-testid="submit-user-button"
                disabled={!canSubmitCreate()}
              >
                Yarat
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-zinc-100 border-b border-zinc-200">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-bold text-zinc-700 uppercase">Login</th>
              <th className="px-6 py-4 text-left text-xs font-bold text-zinc-700 uppercase">Ad Soyad</th>
              <th className="px-6 py-4 text-left text-xs font-bold text-zinc-700 uppercase">Telefon</th>
              <th className="px-6 py-4 text-left text-xs font-bold text-zinc-700 uppercase">Rol</th>
              {showGroupSelection && (
                <th className="px-6 py-4 text-left text-xs font-bold text-zinc-700 uppercase">Qruplar</th>
              )}
              <th className="px-6 py-4 text-left text-xs font-bold text-zinc-700 uppercase">Modullar</th>
              <th className="px-6 py-4 text-center text-xs font-bold text-zinc-700 uppercase">Əməliyyatlar</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200">
            {users.map((u) => (
              <tr key={u.id} className="hover:bg-zinc-50" data-testid={`user-row-${u.id}`}>
                <td className="px-6 py-4">
                  <span className="font-semibold text-zinc-900">{u.username}</span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-zinc-700">{u.full_name || '-'}</span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-zinc-700">{u.phone || '-'}</span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {getUserRoles(u).map(role => (
                      <span 
                        key={role}
                        className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${getRoleBadgeClass(role)}`}
                      >
                        {getRoleLabel(role)}
                      </span>
                    ))}
                  </div>
                </td>
                {showGroupSelection && (
                  <td className="px-6 py-4">
                    <span className="text-zinc-700 text-sm">{getGroupNames(u.groups)}</span>
                  </td>
                )}
                <td className="px-6 py-4">
                  <span className="text-zinc-600 text-xs">{getModuleNames(u.modules)}</span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-center gap-2">
                    <Button
                      onClick={() => onOpenEditDialog(u)}
                      variant="outline"
                      size="sm"
                      data-testid={`edit-user-${u.id}`}
                      title="Məlumatları redaktə et"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={() => onOpenPasswordDialog(u)}
                      variant="outline"
                      size="sm"
                      data-testid={`change-password-${u.id}`}
                      title="Parol dəyişdir"
                    >
                      <Key className="h-4 w-4" />
                    </Button>
                    {u.id !== currentUserId && (
                      <Button
                        onClick={() => onDeleteUser(u.id, u.username)}
                        variant="destructive"
                        size="sm"
                        data-testid={`delete-user-${u.id}`}
                        title="Sil"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={showEditUserDialog} onOpenChange={setShowEditUserDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Məlumatları Redaktə Et</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <form onSubmit={onEditUser} className="space-y-4">
              <div className="text-sm text-zinc-600 mb-4">
                Login: <span className="font-semibold text-zinc-900">{selectedUser.username}</span>
              </div>
              <div>
                <Label htmlFor="edit_full_name">Ad Soyad</Label>
                <Input
                  id="edit_full_name"
                  value={editUser.full_name}
                  onChange={(e) => setEditUser({ ...editUser, full_name: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="edit_phone">Telefon</Label>
                <Input
                  id="edit_phone"
                  value={editUser.phone}
                  onChange={(e) => setEditUser({ ...editUser, phone: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="edit_email">Email</Label>
                <Input
                  id="edit_email"
                  type="email"
                  value={editUser.email}
                  onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              
              {/* Role Selection for Edit */}
              <div>
                <Label>Rol *</Label>
                <p className="text-xs text-zinc-500 mt-1 mb-2">Ən az bir rol seçilməlidir</p>
                <div className="mt-1.5 p-3 bg-zinc-50 rounded-lg border space-y-3">
                  {/* Exclusive roles */}
                  {singleSelectRoles.length > 0 && (
                    <div className="space-y-2">
                      {singleSelectRoles.map(role => (
                        <div key={role} className="flex items-center space-x-2">
                          <Checkbox
                            id={`edit-role-${role}`}
                            checked={isRoleSelected(role, false)}
                            onCheckedChange={() => handleRoleToggle(role, false)}
                            data-testid={`edit-role-checkbox-${role}`}
                          />
                          <label
                            htmlFor={`edit-role-${role}`}
                            className="text-sm font-medium leading-none cursor-pointer"
                          >
                            {getRoleLabel(role)}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Separator */}
                  {singleSelectRoles.length > 0 && multiSelectRoles.length > 0 && (
                    <div className="border-t border-zinc-200 pt-2">
                      <p className="text-xs text-zinc-500 mb-2">Aşağıdakılar birlikdə seçilə bilər:</p>
                    </div>
                  )}
                  
                  {/* Combinable roles */}
                  {multiSelectRoles.length > 0 && (
                    <div className="space-y-2">
                      {multiSelectRoles.map(role => (
                        <div key={role} className="flex items-center space-x-2">
                          <Checkbox
                            id={`edit-role-${role}`}
                            checked={isRoleSelected(role, false)}
                            onCheckedChange={() => handleRoleToggle(role, false)}
                            data-testid={`edit-role-checkbox-${role}`}
                          />
                          <label
                            htmlFor={`edit-role-${role}`}
                            className="text-sm font-medium leading-none cursor-pointer"
                          >
                            {getRoleLabel(role)}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {(editUser.roles || []).length === 0 && (
                  <p className="text-xs text-red-500 mt-1">Ən az bir rol seçin</p>
                )}
              </div>
              
              {/* Group Selection for Edit */}
              {showGroupSelection && availableGroups.length > 0 && (
                <div>
                  <Label className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Qruplar *
                  </Label>
                  <p className="text-xs text-zinc-500 mt-1 mb-2">Ən az bir qrup seçilməlidir</p>
                  <div className="mt-1.5 space-y-2 p-3 bg-zinc-50 rounded-lg border max-h-40 overflow-y-auto">
                    {availableGroups.map(group => (
                      <div key={group.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`edit-group-${group.id}`}
                          checked={isGroupSelected(group.id, false)}
                          onCheckedChange={() => handleGroupToggle(group.id, false)}
                          data-testid={`edit-group-checkbox-${group.id}`}
                        />
                        <label
                          htmlFor={`edit-group-${group.id}`}
                          className="text-sm font-medium leading-none cursor-pointer"
                        >
                          {group.name}
                        </label>
                      </div>
                    ))}
                  </div>
                  {(editUser.groups || []).length === 0 && (
                    <p className="text-xs text-red-500 mt-1">Ən az bir qrup seçin</p>
                  )}
                </div>
              )}

              {/* Module Permissions for Edit */}
              <div>
                <Label className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Modul İcazələri
                </Label>
                <p className="text-xs text-zinc-500 mt-1 mb-2">Boş buraxılsa bütün modullara giriş olacaq</p>
                <div className="mt-1.5 p-3 bg-zinc-50 rounded-lg border space-y-2">
                  <div className="flex items-center space-x-2 pb-2 border-b border-zinc-200">
                    <Checkbox
                      id="edit-module-all"
                      checked={availableModules.every(m => (editUser.modules || []).includes(m.id))}
                      onCheckedChange={() => toggleAllModules(false)}
                      data-testid="edit-module-all"
                    />
                    <label htmlFor="edit-module-all" className="text-sm font-semibold leading-none cursor-pointer">
                      Hamısını seç
                    </label>
                  </div>
                  {availableModules.map(mod => (
                    <div key={mod.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`edit-module-${mod.id}`}
                        checked={(editUser.modules || []).includes(mod.id)}
                        onCheckedChange={() => handleModuleToggle(mod.id, false)}
                        data-testid={`edit-module-${mod.id}`}
                      />
                      <label htmlFor={`edit-module-${mod.id}`} className="text-sm font-medium leading-none cursor-pointer">
                        {mod.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-primary hover:bg-primary/90"
                disabled={!canSubmitEdit()}
              >
                Yenilə
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Parol Dəyişdir</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <form onSubmit={onChangePassword} className="space-y-4">
              <div className="text-sm text-zinc-600 mb-4">
                İstifadəçi: <span className="font-semibold text-zinc-900">{selectedUser.username}</span>
              </div>
              <div>
                <Label htmlFor="new_password">Yeni Parol</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  className="mt-1.5"
                />
              </div>
              <Button type="submit" className="w-full bg-primary hover:bg-primary/90">
                Dəyişdir
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagementPanel;
